import type { Plural } from '../core.js';
import { editor } from './de/editor.js';
import { model } from './de/model.js';
import { server } from './de/server.js';

/** Plural forms are typed explicitly so other languages may add `few`/`many`. */
const p = (forms: Plural): Plural => forms;

/**
 * Reference texts. English and Polish must provide exactly this structure (checked by the
 * type system); placeholders are written as `{name}`, plural counts as `{n}`.
 */
export const de = {
  common: {
    cancel: 'Abbrechen',
    done: 'Fertig',
    ok: 'OK',
    close: 'Schließen',
    copy: 'Kopieren',
    delete: 'Löschen',
    open: 'Öffnen',
    rename: '„{name}“ umbenennen',
    renameAction: 'Umbenennen',
    retry: 'Erneut versuchen',
    loading: 'Lade …',
    name: 'Name',
    email: 'E-Mail',
    password: 'Passwort',
    unexpectedError: 'Ein unerwarteter Fehler ist aufgetreten.',
    language: 'Sprache',
  },
  app: {
    tagline: 'Hallen- und Flächenplanung',
    disclaimer: 'Konzept-/Entwurfsplanung – keine Ausführungs-, Genehmigungs- oder Statikplanung.',
    loadingEditor: 'Editor wird geladen …',
    updateAvailable: 'Eine neue Version ist verfügbar. Bitte vorher speichern.',
    reload: 'Neu laden',
    later: 'Später',
  },
  inspect: {
    toggle: 'Nur ansehen',
    title:
      'Projekte nur ansehen: Änderungen, Vorschaubilder und Arbeitszeit werden nicht gespeichert, Live-Sitzungen nicht betreten.',
    status: 'Nur ansehen – nichts wird gespeichert',
    blocked: 'Nur ansehen ist eingeschaltet: es wird nichts gespeichert.',
  },
  auth: {
    login: 'Anmelden',
    logout: 'Abmelden',
    createAccount: 'Konto erstellen',
    newAccount: 'Neues Konto anlegen',
    haveAccount: 'Ich habe bereits ein Konto',
    registrationPending:
      'Vielen Dank! Ihre Registrierung ist eingegangen. Sobald der Administrator Ihr Konto freigegeben hat, können Sie sich hier anmelden.',
    approvalNotice: 'Neue Konten werden vom Administrator freigegeben.',
    registrationClosed: 'Zugänge vergibt der Administrator. Anfragen an info@ltt-3d.de.',
    accessCode: 'Zugangscode',
    accessCodeContinue: 'Weiter',
    accessCodeHint: 'Den Zugangscode erhalten Sie von uns. Anfragen an info@ltt-3d.de.',
  },
  password: {
    change: 'Passwort ändern',
    current: 'Bisheriges Passwort',
    new: 'Neues Passwort (mind. {min} Zeichen)',
    repeat: 'Neues Passwort wiederholen',
    mismatch: 'Die beiden neuen Passwörter stimmen nicht überein',
    changed: 'Ihr Passwort wurde geändert. Auf anderen Geräten müssen Sie sich neu anmelden.',
  },
  share: {
    button: 'Teilen',
    title: 'Projekt teilen',
    hint: 'Eingeladene müssen bei InduSketch registriert sein. Bearbeiter planen mit, Betrachter sehen das Projekt an und können es begehen.',
    owner: 'Eigentümer',
    you: '(du)',
    roles: { editor: 'Bearbeiten', viewer: 'Nur ansehen' },
    roleAria: 'Rolle von {name}',
    emailLabel: 'E-Mail-Adresse',
    invite: 'Einladen',
    remove: 'Entfernen',
    confirmRemove: 'Zugriff von {name} entfernen?',
    leave: 'Projekt verlassen',
    confirmLeave: 'Das Projekt „{name}“ verlassen? Du verlierst danach den Zugriff.',
    noMembers: 'Noch mit niemandem geteilt.',
    readOnly: 'Nur Ansicht – dieses Projekt ist für dich schreibgeschützt.',
    sharedBy: 'Geteilt von {name}',
    access: { owner: 'Eigentümer', editor: 'Bearbeiter', viewer: 'Nur Ansicht' },
    status: { project: 'im Projekt', online: 'online in InduSketch', offline: 'offline' },
    call: 'Anrufen',
    callAria: '{name} anrufen',
    callAll: 'Alle anrufen',
    onlineElsewhere: p({
      one: '{n} Person ist online, aber nicht im Projekt',
      other: '{n} Personen sind online, aber nicht im Projekt',
    }),
  },
  game: {
    title: 'Die BG kommt!',
    start: 'Spiel: Die BG kommt!',
    intro:
      'Eine Person ist Prüfer der Berufsgenossenschaft, alle anderen sind Mitarbeiter und verstecken sich in der Halle. Wen der Prüfer aus der Nähe sieht, ist erwischt. Wer Helm, Warnweste und Sicherheitsschuhe einsammelt, ist 15 Sekunden sicher. Während des Spiels lässt sich nichts bearbeiten.',
    inspector: 'Prüfer',
    random: 'Zufällig',
    duration: 'Spieldauer',
    minutes: p({ one: '{n} Minute', other: '{n} Minuten' }),
    go: 'Los geht’s',
    noHall: 'Für das Spiel braucht das Projekt eine Halle mit genug freier Fläche.',
    needPlayers: 'Für das Spiel muss mindestens eine weitere Person im Projekt sein.',
    roleInspector: 'Du bist der Prüfer',
    roleStaff: 'Du bist Mitarbeiter',
    spectator: 'Du schaust zu',
    phaseHiding: 'Verstecken',
    phaseHunting: 'Prüfung läuft',
    gate: 'Anmeldung beim Pförtner …',
    gateHint: 'Die Mitarbeiter verstecken sich gerade. Gleich darfst du los.',
    hideHint: 'Versteck dich, der Prüfer kommt gleich!',
    huntInspector: 'Finde die Mitarbeiter: Wen du aus der Nähe siehst, ist erwischt.',
    huntStaff:
      'Sammle Helm, Warnweste und Sicherheitsschuhe ein: komplett ausgerüstet bist du 15 Sekunden sicher.',
    caught: 'Erwischt! Ab zur Sicherheitsbelehrung.',
    immune: 'Vorschriftsmäßig ausgerüstet: noch {s} s sicher',
    free: 'frei',
    caughtShort: 'erwischt',
    protectedShort: 'geschützt',
    ppe: { helmet: 'Helm', vest: 'Warnweste', shoes: 'Sicherheitsschuhe' },
    winInspector: 'Der Prüfer hat alle erwischt!',
    winStaff: 'Die Mitarbeiter haben die Prüfung überstanden!',
    again: 'Nochmal',
    end: 'Spiel beenden',
    endConfirm: 'Spiel für alle beenden?',
    you: '(du)',
  },
  mouse: {
    title: 'Maus & Navigation',
    wheel: 'Mausrad',
    zoomSpeed3d: 'Zoom-Geschwindigkeit 3D',
    zoomSpeed2d: 'Zoom-Geschwindigkeit 2D-Plan',
    zoomToCursor: 'Zum Mauszeiger zoomen (3D)',
    zoomToCursorHint:
      'Das Mausrad zoomt auf den Punkt unter dem Zeiger zu statt auf die Bildmitte.',
    orbitSelection: 'Um ausgewähltes Element drehen (3D)',
    orbitSelectionHint:
      'Ein angeklicktes Element wird zum Drehpunkt; Doppelklick holt es bildfüllend heran.',
    orbitSkipLarge: 'Grundstück, Hallen, Bürogebäude und Böden ausnehmen',
    orbitSkipLargeHint:
      'Sind sie ausgewählt, bleibt der Punkt unter dem Mauszeiger der Drehpunkt – ein Klick neben ein Objekt lässt den Zoom nicht mehr wegspringen.',
    invertWheel: 'Mausrad-Richtung umkehren',
    layout: 'Darstellung',
    bothPanels: 'Beide Seitenleisten gleichzeitig zeigen',
    bothPanelsHint:
      'Aus: Auf dem Tablet ist immer nur eine Seitenleiste offen, damit Plan und 3D-Ansicht mehr Platz haben.',
    drag: 'Ziehen mit der Maus',
    rotateSpeed: 'Drehen der 3D-Ansicht',
    panSpeed: 'Verschieben der 3D-Ansicht',
    lookSpeed: 'Umschauen beim Begehen',
    percent: '{pct} %',
    hint: 'Die Einstellungen gelten für dieses Gerät und diesen Browser.',
    reset: 'Auf Standard zurücksetzen',
  },
  live: {
    online: p({ one: '{n} Person im Projekt', other: '{n} Personen im Projekt' }),
    follow: 'Ansicht von {name} folgen',
    jumpTo: 'Zu {name} springen',
    stopFollow: 'Folgen beenden',
    following: 'Du folgst {name}',
    disconnected: 'Live-Verbindung getrennt – es wird neu verbunden …',
    reconnected: 'Live-Verbindung wiederhergestellt.',
    resynced: 'Eine Änderung passte nicht mehr zum aktuellen Stand und wurde verworfen.',
    walking: 'begeht die Halle',
    viewing2d: 'im 2D-Plan',
    viewing3d: 'in 3D',
  },
  chat: {
    button: 'Chat',
    title: 'Projekt-Chat',
    close: 'Chat schließen',
    minimize: 'Chat minimieren',
    expand: 'Chat öffnen',
    unread: p({ one: '{n} ungelesene Nachricht', other: '{n} ungelesene Nachrichten' }),
    placeholder: 'Nachricht schreiben … (Enter sendet, Umschalt+Enter neue Zeile)',
    send: 'Senden',
    attach: 'Datei anhängen',
    shareView: 'Ansicht teilen',
    shareViewTitle:
      'Bild der aktuellen 3D-Ansicht senden – die anderen können zu dieser Ansicht springen',
    sharingView: 'Ansicht wird aufgenommen …',
    dictate: 'Diktieren',
    dictateTitle:
      'Nachricht einsprechen (Deutsch, Englisch oder Polnisch) – erneut klicken beendet, Esc bricht ab',
    dictateStop: 'Diktat beenden ({time})',
    transcribing: 'Gesprochenes wird erkannt …',
    dictationEmpty: 'Es wurde nichts verstanden – bitte noch einmal sprechen.',
    toPlan: 'Zurück zum Plan',
    placeholderShort: 'Nachricht …',
    download: 'Herunterladen',
    closeImage: 'Bild schließen',
    zoomHint: 'Doppelt tippen zum Vergrößern',
    uploading: 'Wird hochgeladen … {percent} %',
    jumpToView: 'Zur Ansicht springen',
    older: 'Ältere Nachrichten laden',
    empty: 'Noch keine Nachrichten. Schreib die erste – sie wird automatisch übersetzt.',
    notShared: 'Teile das Projekt, damit andere mitlesen, antworten und mitsprechen können.',
    translatedFrom: 'übersetzt aus dem {lang}',
    showOriginal: 'Originaltexte anzeigen',
    original: 'Original',
    showTranslation: 'Übersetzung anzeigen',
    delete: 'Nachricht löschen',
    deleteConfirm: 'Diese Nachricht wirklich löschen?',
    you: 'Du',
    dropHint: 'Dateien hier ablegen',
    call: 'Gespräch',
    callRunning: 'Gespräch läuft',
    callParticipants: 'Teilnehmer: {names}',
    callNoProtocol: 'Kein Protokoll – es wurde nichts übersetzt.',
    callShow: p({ one: '{n} Satz anzeigen', other: '{n} Sätze anzeigen' }),
    callHide: 'Protokoll ausblenden',
    languages: { de: 'Deutschen', en: 'Englischen', pl: 'Polnischen' },
  },
  call: {
    title: 'Sprachanruf mit Dolmetscher',
    button: 'Anruf',
    join: 'Gespräch beitreten',
    start: 'Gespräch starten',
    joining: 'Verbinde …',
    connecting: 'verbindet …',
    direct: 'direkt',
    relayed: 'über Weiterleitung',
    leave: 'Auflegen',
    mute: 'Stummschalten',
    unmute: 'Mikrofon an',
    speaks: 'Ich spreche',
    hears: 'Ich höre',
    original: 'Original leise mithören',
    provider: 'Dolmetscher',
    providers: {
      gemini: 'Gemini – schnell, Stimme folgt dem Sprecher',
      deepl: 'DeepL – Fachbegriffe, feste Stimme',
    },
    noProvider: 'Kein Dolmetscher eingerichtet – ihr hört euch im Original.',
    members: p({ one: '{n} Person im Gespräch', other: '{n} Personen im Gespräch' }),
    consent:
      'Das Gespräch wird übersetzt und als Protokoll im Projekt-Chat gespeichert. Alle Projektmitglieder können es lesen.',
    consentOk: 'Einverstanden, beitreten',
    cancel: 'Abbrechen',
    status: {
      off: 'Keine Übersetzung nötig',
      connecting: 'Dolmetscher verbindet …',
      on: 'Dolmetscher aktiv',
      error: 'Dolmetscher nicht verfügbar',
    },
    micDenied: 'Kein Zugriff aufs Mikrofon – bitte im Browser erlauben.',
    needsLive: 'Gespräche gehen nur in geteilten Projekten mit Live-Verbindung.',
    lostLive: 'Die Verbindung wurde unterbrochen – bitte erneut beitreten.',
    interpreterLost: 'Der Dolmetscher hat die Verbindung verloren.',
    providerChanged: '{name} hat für alle auf {provider} umgestellt.',
    providerForAll: 'Gilt für alle im Gespräch.',
    fallbackOriginal: 'Ihr hört euch jetzt im Original.',
    peerFailed: 'Keine Verbindung möglich – das Netz blockiert sie. Es wird erneut versucht …',
    headphones: 'Tipp: Mit Kopfhörern klingt es am besten.',
    titleShort: 'Sprachanruf',
    settings: 'Sprachen und Dolmetscher',
    incoming: '{name} ruft an',
    running: 'Gespräch läuft mit {names}',
    accept: 'Annehmen',
    decline: 'Ablehnen',
    later: 'Später',
    acceptNote:
      'Mit dem Beitritt stimmst du zu, dass das Gespräch übersetzt und im Chat protokolliert wird.',
    notificationBody: 'Im Projekt annehmen oder ablehnen',
    ringAgain: 'Erneut klingeln',
    ringing: 'klingelt …',
    ringingElsewhere: 'klingelt (anderswo in InduSketch) …',
    offline: 'nicht online',
    incomingProject: '{name} ruft an – Projekt „{project}“',
    acceptSwitch: 'Annehmen wechselt in dieses Projekt; das offene wird vorher gespeichert.',
    callEnded: 'Das Gespräch ist bereits beendet.',
    declined: 'hat abgelehnt',
    notInCall: 'nicht im Gespräch',
    nobodyOnline:
      'Gerade ist sonst niemand im Projekt. Wer es öffnet, sieht einen Hinweis auf das Gespräch.',
    you: 'Du',
    speaking: 'spricht',
  },
  projects: {
    title: 'Projekte',
    admin: 'Verwaltung',
    newPlaceholder: 'Name des neuen Projekts, z. B. „Halle Nord“',
    projectName: 'Projektname',
    create: 'Neues Projekt',
    import: 'Projekt importieren',
    importing: 'Importiere …',
    importHint: 'Exportierte InduSketch-Datei als neues Projekt öffnen',
    search: 'Suchen …',
    searchLabel: 'Projekte durchsuchen',
    sort: 'Sortierung',
    sortUpdated: 'Zuletzt geändert',
    sortName: 'Name',
    loading: 'Lade Projekte …',
    empty: 'Noch keine Projekte vorhanden.',
    noMatch: 'Keine Treffer.',
    variantsOf: 'Varianten von {name}',
    newName: 'Neuer Projektname',
    changed: 'Geändert {date}',
    variant: 'Variante',
    confirmDelete: 'Projekt „{name}“ wirklich löschen?',
    actions: 'Aktionen für „{name}“',
    createVariant: 'Variante erstellen',
    variants: p({ one: '{n} Variante', other: '{n} Varianten' }),
    emptyTitle: 'Noch keine Projekte',
    emptyHint:
      'Geben Sie oben einen Namen ein und wählen Sie „Neues Projekt“ – oder importieren Sie eine exportierte InduSketch-Datei.',
    userMenu: 'Benutzermenü',
    activeNow: 'Gerade in Bearbeitung: {names}',
  },
  assets: {
    title: 'Meine 3D-Modelle',
    empty: 'Noch keine eigenen Modelle. Eine GLB-Datei hierher ziehen oder hochladen.',
    upload: 'Modell hochladen',
    uploading: 'Wird hochgeladen …',
    dropHint: 'GLB-Datei hier ablegen',
    dimensions: 'Breite × Tiefe × Höhe',
    actions: 'Aktionen für „{name}“',
    triangles: p({ one: '{n} Dreieck', other: '{n} Dreiecke' }),
    newName: 'Neuer Name',
    confirmDelete: 'Modell „{name}“ löschen?',
    unused: 'Es wird in keinem Projekt verwendet.',
    usedIn: 'Es wird noch verwendet in:',
    placeholderAfter: 'Dort erscheint danach eine Platzhalter-Kiste in gleicher Größe.',
  },
  topbar: {
    toProjects: 'Zur Projektliste',
    toggleTools: 'Werkzeuge ein-/ausblenden',
    toggleProperties: 'Eigenschaften ein-/ausblenden',
    more: 'Mehr',
    unsaved: 'Ungespeicherte Änderungen',
    unsavedShort: 'ungespeichert',
    view: 'Ansicht',
    plan2d: '2D-Plan',
    view3d: '3D',
    top: 'Oben',
    front: 'Vorne',
    side: 'Seite',
    fit: 'Alles zeigen',
    findFar: 'Entferntestes Element',
    findFarTitle:
      'Springt zum Element, das am weitesten außerhalb liegt (z. B. versehentlich weit weg abgelegt); erneut klicken für das nächste',
    farFound: '{name}: {distance} {reference} ({k} von {n})',
    farReference: {
      sites: 'außerhalb des Grundstücks',
      buildings: 'neben den Gebäuden',
      center: 'von der Projektmitte entfernt',
    },
    farNone: 'Alle Elemente liegen auf dem Grundstück bzw. bei den Gebäuden.',
    undo: 'Rückgängig',
    undoTitle: 'Rückgängig (Strg+Z)',
    redo: 'Wiederholen',
    redoTitle: 'Wiederholen (Strg+Y)',
    versions: 'Versionen',
    versionsTitle: 'Versionsverlauf',
    pdf: 'PDF',
    pdfTitle: 'PDF-Entwurfsbericht erstellen',
    export: 'Export',
    exporting: 'Export …',
    exportTitle: 'Projekt als Datei exportieren (inkl. eigener 3D-Modelle), z. B. zum Weitergeben',
    save: 'Speichern',
    saving: 'Speichert …',
    saveError: 'Nicht gespeichert – neuer Versuch folgt',
    conflict: 'Konflikt',
    pending: 'Änderungen …',
    savedAt: 'Gespeichert {time}',
  },
  versions: {
    title: 'Versionsverlauf',
    labelPlaceholder: 'Bezeichnung, z. B. „Abstimmung mit Hallenbauer“',
    labelAria: 'Bezeichnung der Version',
    create: 'Version speichern',
    loading: 'Lade Versionen …',
    empty: 'Noch keine Versionen. Automatische Sicherungen entstehen beim Speichern.',
    auto: 'Automatische Sicherung',
    autoShort: 'Automatisch',
    meta: '{date} · {user} · Stand {revision}',
    restore: 'Wiederherstellen',
    confirmRestore:
      'Version „{label}“ wiederherstellen? Der aktuelle Stand wird vorher als Version gesichert.',
    saveFirst: 'Bitte zuerst speichern bzw. den Konflikt lösen.',
    delete: 'Version löschen',
    confirmDelete: 'Version „{label}“ endgültig löschen?',
    prune: 'Automatische Sicherungen älter als {days} Tage löschen ({n})',
    confirmPrune:
      'Alle automatischen Sicherungen älter als {days} Tage endgültig löschen? Benannte Versionen bleiben erhalten.',
    pruned: p({ one: '{n} Sicherung gelöscht.', other: '{n} Sicherungen gelöscht.' }),
  },
  conflict: {
    title: 'Projekt wurde zwischenzeitlich geändert',
    text: 'Jemand (oder ein anderer Tab) hat dieses Projekt gespeichert, nachdem du es geöffnet hast. Deine Änderungen wurden noch nicht gespeichert und nichts wurde überschrieben.',
    saveCopy: 'Meine Version als Kopie speichern',
    loadServer: 'Stand vom Server laden (meine Änderungen verwerfen)',
    overwrite: 'Trotzdem überschreiben',
    copyName: '{name} (Kopie)',
  },
  admin: {
    title: 'Verwaltung',
    back: '← Projekte',
    tabs: 'Bereiche',
    tabUsers: 'Benutzer',
    tabProjects: 'Projekte',
    tabAssets: 'Modelle',
    tabAudit: 'Protokoll',
    tabPrices: 'Preise',
    pricesIntro:
      'Einheitspreise (netto, €) und Zuschläge der Kalkulation. Jedes Projekt rechnet mit dem Standardkatalog oder dem gewählten und kann einzelne Preise selbst überschreiben. Leere Felder verwenden den Richtwert.',
    pricesCatalogs: 'Preiskataloge',
    pricesDefaultBadge: 'Standard',
    pricesCopy: '+ Kopie anlegen',
    pricesCopyName: 'Kopie von {name}',
    pricesName: 'Name',
    pricesCountry: 'Land',
    pricesIsDefault: 'Standard für alle Projekte ohne eigene Auswahl',
    pricesItem: 'Position',
    pricesGuide: 'Richtwert',
    pricesCatalogPrice: 'Katalogpreis',
    pricesSave: 'Katalog speichern',
    pricesDelete: 'Katalog löschen',
    pricesConfirmDelete:
      'Preiskatalog „{name}“ löschen? Projekte, die ihn gewählt haben, rechnen danach mit dem Standardkatalog.',
    pricesSaved: 'Gespeichert',
    pricesUnsaved: 'Nicht gespeicherte Änderungen',
    pricesCatalog: 'Katalog',
    pricesUnitPrices: 'Einheitspreise',
    pricesSurcharges: 'Zuschläge',
    tabActivity: 'Aktivität',
    colLastActive: 'Zuletzt aktiv',
    never: 'noch nie',
    activitySummary: 'Nutzer im Überblick',
    activityTimeline: 'Verlauf',
    colDevice: 'Gerät',
    colLocation: 'Ort',
    colIp: 'IP-Adresse',
    colWorkGroup: 'Arbeitszeit',
    col30Group: 'Letzte 30 Tage',
    colWork7: '7 Tage',
    colWork30: '30 Tage',
    colLogins: 'Anmeldungen',
    colFailed: 'Fehlversuche',
    online: 'Gerade aktiv',
    activeIn: 'gerade in „{project}“',
    sortBy: 'Sortieren nach',
    sortAsc: 'aufsteigend',
    sortDesc: 'absteigend',
    colUser: 'Nutzer',
    colEvent: 'Ereignis',
    activityKinds: {
      login: 'Anmeldung',
      logout: 'Abmeldung',
      'login-failed': 'Fehlversuch',
      work: 'Arbeitssitzung',
    },
    workIn: '{duration} in „{project}“',
    workPlain: '{duration} im Editor',
    minutes: '{n} min',
    hoursMinutes: '{h} h {m} min',
    activityEmpty: 'Noch keine Aktivität erfasst.',
    activityNote:
      'Erfasst werden An- und Abmeldungen, Fehlversuche und die aktive Arbeitszeit im Editor. Einträge werden nach {days} Tagen automatisch gelöscht. IP-Geolokalisierung: DB-IP.com (CC BY 4.0).',
    filterActivity: 'Nach Name, E-Mail, Ort oder IP suchen …',
    filterActivityAria: 'Aktivität filtern',
    pending: 'Wartet auf Freigabe ({n})',
    registered: 'registriert {date}',
    approve: 'Freigeben',
    reject: 'Ablehnen',
    confirmReject: 'Registrierung von {email} ablehnen? Das Konto wird gelöscht.',
    confirmReset: 'Neues Passwort für {email} erzeugen? Das bisherige gilt dann nicht mehr.',
    newPassword: 'Neues Passwort',
    accountCreated: 'Konto angelegt',
    oneTimePassword:
      'Einmal-Passwort für {email}. Es wird nur jetzt angezeigt – bitte sicher weitergeben. Der Nutzer kann es nach der Anmeldung unter „Passwort ändern“ ersetzen.',
    colName: 'Name',
    colEmail: 'E-Mail',
    colRole: 'Rolle',
    colProjects: 'Projekte',
    colSince: 'Seit',
    colStatus: 'Status',
    roleAdmin: 'Administrator',
    roleUser: 'Benutzer',
    disabled: 'gesperrt',
    active: 'aktiv',
    ownAccount: 'eigenes Konto',
    makeUser: 'Zum Benutzer',
    makeAdmin: 'Zum Admin',
    unlock: 'Entsperren',
    lock: 'Sperren',
    resetPassword: 'Passwort zurücksetzen',
    addUser: '+ Nutzer anlegen',
    createUser: 'Nutzer anlegen',
    role: 'Rolle',
    createAndPassword: 'Anlegen und Passwort erzeugen',
    filterProjects: 'Nach Projekt oder Besitzer suchen …',
    filterProjectsAria: 'Projekte filtern',
    colProject: 'Projekt',
    colOwner: 'Besitzer',
    colRevision: 'Stand',
    colSize: 'Größe',
    colChanged: 'Geändert',
    uploading: 'Wird hochgeladen …',
    uploadSystem: 'Modell zur Systembibliothek hinzufügen (GLB)',
    colModel: 'Modell',
    colLibrary: 'Bibliothek',
    colDimensions: 'Maße (B × T × H)',
    colUploaded: 'Hochgeladen',
    system: 'System',
    auditEmpty: 'Noch keine Einträge.',
    statUsers: 'Aktive Benutzer',
    statPending: 'Offene Freigaben',
    statProjects: 'Projekte',
    statModels: 'Modelle',
    statStorage: 'Speicher (Modelle und Projekte)',
    filterUsers: 'Nach Name oder E-Mail suchen …',
    filterUsersAria: 'Benutzer filtern',
    userActions: 'Aktionen für {email}',
    systemLibrary: 'Systembibliothek',
    userModels: 'Modelle der Nutzer',
    noUserModels: 'Die Nutzer haben noch keine eigenen Modelle hochgeladen.',
    noSystemModels: 'Noch keine Modelle in der Systembibliothek.',
    uploadedBy: 'von {name} · {date}',
    showMore: 'Weitere {n} anzeigen',
    colTime: 'Zeit',
    colAdmin: 'Administrator',
    colAction: 'Aktion',
    colTarget: 'Ziel',
    colDetails: 'Details',
    colActor: 'Ausgeführt von',
    filterAudit: 'Nach Person, Aktion oder Ziel suchen …',
    filterAuditAria: 'Protokoll filtern',
    allActions: 'Alle Aktionen',
    actionFilter: 'Aktion filtern',
    loadOlder: 'Ältere Einträge laden',
    targetTypes: {
      project: 'Projekt',
      user: 'Benutzer',
      asset: 'Modell',
      'price-catalog': 'Preiskatalog',
    },
    detailRole: 'Rolle: {role}',
    detailWith: 'mit {name}',
    detailRevision: 'ab Stand {n}',
    detailDefault: 'als Standard',
    detailLocked: 'gesperrt',
    detailUnlocked: 'entsperrt',
    detailApproved: 'freigegeben',
    actions: {
      'project.update': 'Projekt geändert',
      'project.live': 'Live bearbeitet',
      'project.meta': 'Projekt umbenannt',
      'project.delete': 'Projekt gelöscht',
      'project.restore': 'Version wiederhergestellt',
      'project.share': 'Projekt geteilt',
      'project.unshare': 'Freigabe entfernt',
      'asset.delete': 'Modell gelöscht',
      'asset.system-upload': 'Systemmodell hochgeladen',
      'user.update': 'Benutzer geändert',
      'user.create': 'Benutzer angelegt',
      'user.reject': 'Registrierung abgelehnt',
      'user.password-reset': 'Passwort zurückgesetzt',
      'price-catalog.create': 'Preiskatalog angelegt',
      'price-catalog.update': 'Preiskatalog geändert',
      'price-catalog.delete': 'Preiskatalog gelöscht',
    },
  },
  network: {
    offline: 'Keine Verbindung zum Server',
    serverError: 'Serverfehler ({status})',
    invalidResponse: 'Unerwartete Serverantwort',
  },
  editor,
  model,
  server,
};

export type Messages = typeof de;
